  
  import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
  } from "react";
import { toast } from "react-hot-toast";
import useCatalogue from "../../Services/Catalogue/useCatalogue";

const PAGE_SIZE = 160;

const VEG_ICON_URL = "[https://anlook.com/images/Veg.png]";
const NON_VEG_ICON_URL = "[https://anlook.com/images/Non_veg.png]";

const ICON_DISH_OPTIONS = [
  {
    label: "Veg",
    value: VEG_ICON_URL,
  },
  {
    label: "Non Veg",
    value: NON_VEG_ICON_URL,
  },
];

const EDITABLE_COLUMNS = new Set([
  "item_name",
  "item_description",
  "category_alias",
  "size",
  "size_id",
  "icon_dish",
  "iconDish",
  "inStock",
  "visibility",
  "stock",
  "price",
  "car_order_price",
  "home_delivery_price",
  "takeaway_price",
  "dining_price",
  "room_order_price",
]);

const API_PAYLOAD_COLUMNS = [
  "pk_item_id",
  "item_name",
  "item_description",
  "category_alias",
  "size",
  "size_id",
  "price",
  "inStock",
  "visibility",
  "stock",
  "home_delivery_price",
  "takeaway_price",
  "dining_price",
  "room_order_price",
  "car_order_price",
  "iconDish",
];
const PRICE_COLUMNS = new Set([
  "price",
  "home_delivery_price",
  "takeaway_price",
  "dining_price",
  "room_order_price",
  "car_order_price",
]);

const BULK_SELECT_COLUMNS = new Set([
  "icon_dish",
  // "iconDish",
  "inStock",
  "visibility",
]);
const PREFERRED_COLUMNS = [
  "pk_item_id",
  "fk_user_id",
  "item_id",
  "item_name",
  "item_description",
  "icon_dish",
  // "iconDish",
  "category_name",
  "category_alias",
  "sub_category",
  "price",
  "discount",
  "discount_price",
  "size",
  "size_id",
  "item_tax",
  "home_delivery_price",
  "takeaway_price",
  "dining_price",
  "room_order_price",
  "car_order_price",
  "stock",
  "inStock",
  "status",
  "visibility",
  "barcode_number",
  "rack_no",
  "packaging_charge",
  "printer_code",
  "printer_alias",
  "item_category",
  "priority",
  "expiry_date",
];

function displayValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function getStorageKey(userID) {
  return `catalogue_table_pending_changes_${userID || "unknown"}`;
}

function getRowPk(row) {
  return row?.pk_item_id ? String(row.pk_item_id) : "";
}

function isNumericLike(key, value) {
  const k = String(key || "").toLowerCase();

  if (
    k.includes("price") ||
    k.includes("tax") ||
    k.includes("discount") ||
    k.includes("stock") ||
    k.includes("priority")
  ) {
    return true;
  }

  return typeof value === "number";
}

function toApiPayload(row) {
  return {
    pk_item_id: String(row?.pk_item_id ?? ""),
    item_name: String(row?.item_name ?? ""),
    item_description: String(row?.item_description ?? ""),
    size: String(row?.size ?? ""),
    size_id: String(row?.size_id ?? ""),
    category_alias:String(row?.category_alias ?? ""),
    price: String(row?.price ?? ""),
    inStock: String(row?.inStock ?? ""),
    visibility: String(row?.visibility ?? ""),
    stock: String(row?.stock ?? ""),
    home_delivery_price: Number(row?.home_delivery_price ?? 0),
    takeaway_price: Number(row?.takeaway_price ?? 0),
    dining_price: Number(row?.dining_price ?? 0),
    room_order_price: Number(row?.room_order_price ?? 0),
    car_order_price: Number(row?.car_order_price ?? 0),
    iconDish: String(row?.iconDish ?? row?.icon_dish ?? ""),
  };
}

function normalizeForCompare(value) {
  if (value === null || value === undefined) return "";
  return String(value);
}

function hasEditableChanges(originalRow, editedRow) {
  if (!originalRow || !editedRow) return false;

  return Array.from(EDITABLE_COLUMNS).some((key) => {
    return (
      normalizeForCompare(originalRow?.[key]) !==
      normalizeForCompare(editedRow?.[key])
    );
  });
}

function mergePendingRows(rows, pendingMap) {
  if (!pendingMap || Object.keys(pendingMap).length === 0) return rows;

  return rows.map((row) => {
    const pk = getRowPk(row);
    const pending = pendingMap[pk];

    if (!pk || !pending) return row;

    const normalizedPending = { ...pending };

    if (normalizedPending.iconDish !== undefined) {
      normalizedPending.icon_dish = normalizedPending.iconDish;
    }

    return {
      ...row,
      ...normalizedPending,
    };
  });
}

export default function CatalogueTableView({ userID }) {
  const {
    getCatalogueTableData,
    updateCatalogueTableItems,
    getAdminUomMaster,
    getAdminSizeMaster,
    searchCatalogueItems,
  } = useCatalogue();

  const storageKey = useMemo(() => getStorageKey(userID), [userID]);

  const [rows, setRows] = useState([]);
  const [originalRowsMap, setOriginalRowsMap] = useState({});
  const [pendingChanges, setPendingChanges] = useState({});
  const [changedCells, setChangedCells] = useState({});

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(1);

  const [selectedCell, setSelectedCell] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [draftValue, setDraftValue] = useState("");

  const [searchText, setSearchText] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [columnSearchText, setColumnSearchText] = useState("");
  const [showColumnPanel, setShowColumnPanel] = useState(false);
  const [showReadonlyColumns, setShowReadonlyColumns] = useState(true);
  const [visibleColumnMap, setVisibleColumnMap] = useState({});

  const [uomList, setUomList] = useState([]);
  const [selectedUomCode, setSelectedUomCode] = useState("");
  const [sizeOptions, setSizeOptions] = useState([]);
  const [uomLoading, setUomLoading] = useState(false);
  const [sizeLoading, setSizeLoading] = useState(false);

  const [priceAdjustment, setPriceAdjustment] = useState(null);
  const [adjustmentType, setAdjustmentType] = useState("increase");
  const [adjustmentPercentage, setAdjustmentPercentage] = useState("");
  const [roundOff, setRoundOff] = useState(false);

  const [bulkColumnAction, setBulkColumnAction] = useState(null);
  const [bulkValue, setBulkValue] = useState("");

  const loaderRef = useRef(null);
  const inputRef = useRef(null);
  const cellRefs = useRef({});
  const tableScrollRef = useRef(null);
  const topScrollRef = useRef(null);
  const columnPanelRef = useRef(null);

  const isEditable = useCallback((key) => {
    return EDITABLE_COLUMNS.has(String(key || ""));
  }, []);

  const readSessionPending = useCallback(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }, [storageKey]);

  const writeSessionPending = useCallback(
    (next) => {
      try {
        if (!next || Object.keys(next).length === 0) {
          sessionStorage.removeItem(storageKey);
        } else {
          sessionStorage.setItem(storageKey, JSON.stringify(next));
        }
      } catch (e) {
        console.error("sessionStorage write error:", e);
      }
    },
    [storageKey],
  );

  const allColumns = useMemo(() => {
    if (!rows.length) return PREFERRED_COLUMNS;

    const apiKeys = Array.from(
      new Set(rows.flatMap((row) => Object.keys(row || {}))),
    );

    return [
      ...PREFERRED_COLUMNS.filter((key) => apiKeys.includes(key)),
      ...apiKeys.filter((key) => !PREFERRED_COLUMNS.includes(key)),
    ];
  }, [rows]);

  useEffect(() => {
    setVisibleColumnMap((prev) => {
      const next = { ...prev };

      allColumns.forEach((col) => {
        if (next[col] === undefined) next[col] = true;
      });

      Object.keys(next).forEach((key) => {
        if (!allColumns.includes(key)) delete next[key];
      });

      return next;
    });
  }, [allColumns]);

  const readonlyColumns = useMemo(() => {
    return allColumns.filter((col) => !isEditable(col));
  }, [allColumns, isEditable]);

  const visibleColumns = useMemo(() => {
    return allColumns.filter((col) => {
      const userSelected = visibleColumnMap[col] !== false;
      const allowedByReadonlyToggle = showReadonlyColumns || isEditable(col);

      return userSelected && allowedByReadonlyToggle;
    });
  }, [allColumns, visibleColumnMap, showReadonlyColumns, isEditable]);

  // UOM and Size Loading And use Effects
  useEffect(() => {
    const loadUom = async () => {
      setUomLoading(true);

      const res = await getAdminUomMaster({ sizeType: "0" });

      if (res?.success) {
        setUomList(Array.isArray(res.data) ? res.data : []);
      } else {
        toast.error(res?.message || "Failed to load UOM");
      }

      setUomLoading(false);
    };

    loadUom();
  }, []);

  useEffect(() => {
    const loadSizes = async () => {
      if (!selectedUomCode) {
        setSizeOptions([]);
        return;
      }

      setSizeLoading(true);

      const res = await getAdminSizeMaster({
        sizeType: selectedUomCode,
      });

      if (res?.success) {
        setSizeOptions(Array.isArray(res.data) ? res.data : []);
      } else {
        setSizeOptions([]);
        toast.error(res?.message || "Failed to load sizes");
      }

      setSizeLoading(false);
    };

    loadSizes();
  }, [selectedUomCode]);
  // column filteration and shown
  const filteredColumnOptions = useMemo(() => {
    const q = columnSearchText.trim().toLowerCase();

    return allColumns.filter((col) => {
      if (!q) return true;
      return col.toLowerCase().includes(q);
    });
  }, [allColumns, columnSearchText]);

const filteredRows = useMemo(() => {
  return rows.map((row, sourceRowIndex) => ({
    row,
    sourceRowIndex,
    rowKey: getRowPk(row) || `row-${sourceRowIndex}`,
  }));
}, [rows]); 

  const pendingRequests = useMemo(() => {
    return Object.values(pendingChanges);
  }, [pendingChanges]);

  const pendingCount = pendingRequests.length;

  const changedCellsCount = useMemo(() => {
    return Object.keys(changedCells).length;
  }, [changedCells]);

  const focusCell = useCallback((rowIndex, colIndex) => {
    const refKey = `${rowIndex}-${colIndex}`;
    const el = cellRefs.current[refKey];

    if (el) {
      el.focus();
      el.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  }, []);

  const selectCell = useCallback(
    (rowIndex, colIndex) => {
      const key = visibleColumns[colIndex];
      const rowInfo = filteredRows[rowIndex];

      if (!key || !rowInfo) return;

      setSelectedCell({
        rowIndex,
        colIndex,
        key,
        sourceRowIndex: rowInfo.sourceRowIndex,
        rowKey: rowInfo.rowKey,
      });
    },
    [visibleColumns, filteredRows],
  );

  const startEdit = useCallback(
    (rowIndex, colIndex) => {
      const key = visibleColumns[colIndex];
      const rowInfo = filteredRows[rowIndex];

      if (!key || !rowInfo || !isEditable(key)) return;

      const currentValue = rowInfo.row?.[key];

      setSelectedCell({
        rowIndex,
        colIndex,
        key,
        sourceRowIndex: rowInfo.sourceRowIndex,
        rowKey: rowInfo.rowKey,
      });

      setEditingCell({
        rowIndex,
        colIndex,
        key,
        sourceRowIndex: rowInfo.sourceRowIndex,
        rowKey: rowInfo.rowKey,
      });

      setDraftValue(currentValue ?? "");
    },
    [visibleColumns, filteredRows, isEditable],
  );

  const stopEdit = useCallback(() => {
    setEditingCell(null);
    setDraftValue("");
  }, []);
  const commitCellPatch = useCallback(
    ({ patch, moveDirection = null }) => {
      if (!editingCell) return;

      const { rowIndex, colIndex, sourceRowIndex } = editingCell;
      const currentRow = rows[sourceRowIndex];

      if (!currentRow) {
        stopEdit();
        return;
      }

      const pk = getRowPk(currentRow);

      if (!pk) {
        toast.error("pk_item_id missing. Cannot track this row.");
        stopEdit();
        return;
      }

      const updatedRow = {
        ...currentRow,
        ...patch,
      };

      setRows((prev) =>
        prev.map((row, idx) => (idx === sourceRowIndex ? updatedRow : row)),
      );

      const originalRow = originalRowsMap[pk];

      setPendingChanges((prev) => {
        const next = { ...prev };

        if (hasEditableChanges(originalRow, updatedRow)) {
          next[pk] = toApiPayload(updatedRow);
        } else {
          delete next[pk];
        }

        writeSessionPending(next);
        return next;
      });

      setChangedCells((prev) => {
        const next = { ...prev };

        Array.from(EDITABLE_COLUMNS).forEach((col) => {
          const changed =
            normalizeForCompare(originalRow?.[col]) !==
            normalizeForCompare(updatedRow?.[col]);

          const cellKey = `${pk}-${col}`;

          if (changed) next[cellKey] = true;
          else delete next[cellKey];
        });

        return next;
      });

      setEditingCell(null);
      setDraftValue("");

      if (moveDirection === "down") {
        const nextRow = Math.min(rowIndex + 1, filteredRows.length - 1);
        setTimeout(() => {
          selectCell(nextRow, colIndex);
          focusCell(nextRow, colIndex);
        }, 0);
      } else if (moveDirection === "right") {
        const nextCol = Math.min(colIndex + 1, visibleColumns.length - 1);
        setTimeout(() => {
          selectCell(rowIndex, nextCol);
          focusCell(rowIndex, nextCol);
        }, 0);
      } else {
        setTimeout(() => {
          selectCell(rowIndex, colIndex);
          focusCell(rowIndex, colIndex);
        }, 0);
      }
    },
    [
      editingCell,
      rows,
      originalRowsMap,
      writeSessionPending,
      filteredRows.length,
      visibleColumns.length,
      selectCell,
      focusCell,
      stopEdit,
    ],
  );

  const saveEdit = useCallback(
    (moveDirection = null) => {
      if (!editingCell) return;

      commitCellPatch({
        patch: {
          [editingCell.key]: draftValue,
        },
        moveDirection,
      });
    },
    [editingCell, draftValue, commitCellPatch],
  );
  // const saveEdit = useCallback(
  //   (moveDirection = null) => {
  //     if (!editingCell) return;

  //     const { rowIndex, colIndex, key, sourceRowIndex, rowKey } = editingCell;
  //     const currentRow = rows[sourceRowIndex];

  //     if (!currentRow) {
  //       stopEdit();
  //       return;
  //     }

  //     const pk = getRowPk(currentRow);

  //     if (!pk) {
  //       toast.error("pk_item_id missing. Cannot track this row.");
  //       stopEdit();
  //       return;
  //     }

  //     const updatedRow = {
  //       ...currentRow,
  //       [key]: draftValue,
  //     };

  //     setRows((prev) =>
  //       prev.map((row, idx) => (idx === sourceRowIndex ? updatedRow : row)),
  //     );

  //     const originalRow = originalRowsMap[pk];

  //     setPendingChanges((prev) => {
  //       const next = { ...prev };

  //       if (hasEditableChanges(originalRow, updatedRow)) {
  //         next[pk] = toApiPayload(updatedRow);
  //       } else {
  //         delete next[pk];
  //       }

  //       writeSessionPending(next);
  //       return next;
  //     });

  //     setChangedCells((prev) => {
  //       const next = { ...prev };

  //       Array.from(EDITABLE_COLUMNS).forEach((col) => {
  //         const changed =
  //           normalizeForCompare(originalRow?.[col]) !==
  //           normalizeForCompare(updatedRow?.[col]);

  //         const cellKey = `${pk}-${col}`;

  //         if (changed) next[cellKey] = true;
  //         else delete next[cellKey];
  //       });

  //       return next;
  //     });

  //     setEditingCell(null);
  //     setDraftValue("");

  //     if (moveDirection === "down") {
  //       const nextRow = Math.min(rowIndex + 1, filteredRows.length - 1);
  //       setTimeout(() => {
  //         selectCell(nextRow, colIndex);
  //         focusCell(nextRow, colIndex);
  //       }, 0);
  //     } else if (moveDirection === "right") {
  //       const nextCol = Math.min(colIndex + 1, visibleColumns.length - 1);
  //       setTimeout(() => {
  //         selectCell(rowIndex, nextCol);
  //         focusCell(rowIndex, nextCol);
  //       }, 0);
  //     } else {
  //       setTimeout(() => {
  //         selectCell(rowIndex, colIndex);
  //         focusCell(rowIndex, colIndex);
  //       }, 0);
  //     }
  //   },
  //   [
  //     editingCell,
  //     rows,
  //     draftValue,
  //     originalRowsMap,
  //     writeSessionPending,
  //     filteredRows.length,
  //     visibleColumns.length,
  //     selectCell,
  //     focusCell,
  //     stopEdit,
  //   ],
  // );

  const moveSelection = useCallback(
    (direction) => {
      if (!selectedCell) return;

      const { rowIndex, colIndex } = selectedCell;

      let nextRow = rowIndex;
      let nextCol = colIndex;

      if (direction === "up") nextRow = Math.max(0, rowIndex - 1);
      if (direction === "down") {
        nextRow = Math.min(filteredRows.length - 1, rowIndex + 1);
      }
      if (direction === "left") nextCol = Math.max(0, colIndex - 1);
      if (direction === "right") {
        nextCol = Math.min(visibleColumns.length - 1, colIndex + 1);
      }

      selectCell(nextRow, nextCol);
      setTimeout(() => focusCell(nextRow, nextCol), 0);
    },
    [
      selectedCell,
      filteredRows.length,
      visibleColumns.length,
      selectCell,
      focusCell,
    ],
  );



  const fetchPage = useCallback(
    async ({ nextOffset = 1, append = false } = {}) => {
      if (!userID) return;

      if (append) setLoadingMore(true);
      else setLoading(true);

      const res = await getCatalogueTableData({
        fk_user_id: userID,
        data_record_limit: PAGE_SIZE,
        data_record_offset: nextOffset,
      });

      if (res?.success) {
        const incoming = Array.isArray(res.data) ? res.data : [];
        const sessionPending = readSessionPending();

        setOriginalRowsMap((prev) => {
          const next = append ? { ...prev } : {};

          incoming.forEach((row, idx) => {
            const pk = getRowPk(row);
            if (pk) next[pk] = row;
            else next[`fallback-${nextOffset}-${idx}`] = row;
          });

          return next;
        });

        const mergedIncoming = mergePendingRows(incoming, sessionPending);

        setRows((prev) =>
          append ? [...prev, ...mergedIncoming] : mergedIncoming,
        );

        setPendingChanges(sessionPending);

        setChangedCells(() => {
          const next = {};

          Object.keys(sessionPending).forEach((pk) => {
            const pendingRow = sessionPending[pk];
            EDITABLE_COLUMNS.forEach((col) => {
              next[`${pk}-${col}`] = true;
            });

            API_PAYLOAD_COLUMNS.forEach((col) => {
              if (
                pendingRow?.[col] !== undefined &&
                EDITABLE_COLUMNS.has(col)
              ) {
                next[`${pk}-${col}`] = true;
              }
            });
          });

          return next;
        });

        setHasMore(incoming.length === PAGE_SIZE);
        setOffset(nextOffset);
      } else {
        if (!append) setRows([]);
        setHasMore(false);
        toast.error(res?.message || "Failed to load catalogue table");
      }

      if (append) setLoadingMore(false);
      else setLoading(false);
    },
    [getCatalogueTableData, userID, readSessionPending],
  );


    const handleCatalogueSearch = useCallback(
  async (text) => {
    const value = text.trim();

    if (!value) {
      await fetchPage({
        nextOffset: 1,
        append: false,
      });
      return;
    }

    setSearchLoading(true);
    setHasMore(false);
    setSelectedCell(null);
    setEditingCell(null);

    try {
      const res = await searchCatalogueItems({
        text: value,
      });

      if (res?.success) {
const incoming = Array.isArray(res.data) ? res.data : [];

const sessionPending = readSessionPending();

const clientRows = incoming;
        setOriginalRowsMap(() => {
          const next = {};

          clientRows.forEach((row) => {
            const pk = getRowPk(row);

            if (pk) {
              next[pk] = row;
            }
          });

          return next;
        });

        const mergedRows = mergePendingRows(
          clientRows,
          sessionPending
        );

        setRows(mergedRows);
        setPendingChanges(sessionPending);

        setChangedCells(() => {
          const next = {};

          Object.keys(sessionPending).forEach((pk) => {
            EDITABLE_COLUMNS.forEach((col) => {
              next[`${pk}-${col}`] = true;
            });
          });

          return next;
        });

        setOffset(1);
      } else {
        setRows([]);
        setHasMore(false);
        toast.error(
          res?.message || "Failed to search catalogue"
        );
      }
    } catch (error) {
      console.error("Catalogue search error:", error);

      setRows([]);
      setHasMore(false);

      toast.error("Failed to search catalogue");
    } finally {
      setSearchLoading(false);
    }
  },
  [
    searchCatalogueItems,
    userID,
    readSessionPending,
    fetchPage,
  ],
);

useEffect(() => {
  const value = searchText.trim();

  if (!value) {
    return;
  }

  const timer = setTimeout(() => {
    handleCatalogueSearch(value);
  }, 400);

  return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [searchText]);


  const applyPriceAdjustment = useCallback(() => {
    if (!priceAdjustment) return;

    const percentage = Number(adjustmentPercentage);

    if (!Number.isFinite(percentage) || percentage < 0) {
      toast.error("Please enter a valid percentage");
      return;
    }

    const column = priceAdjustment;

    setRows((prevRows) => {
      const updatedRows = prevRows.map((row) => {
        const currentValue = Number(row?.[column]);

        if (!Number.isFinite(currentValue)) {
          return row;
        }

        const multiplier =
          adjustmentType === "increase"
            ? 1 + percentage / 100
            : 1 - percentage / 100;

        let newValue = currentValue * multiplier;

        // Round to nearest whole number
        if (roundOff) {
          newValue = Math.round(newValue);
        } else {
          newValue = Number(newValue.toFixed(2));
        }

        return {
          ...row,
          [column]: newValue,
        };
      });

      setPendingChanges((prevPending) => {
        const nextPending = { ...prevPending };

        updatedRows.forEach((updatedRow) => {
          const pk = getRowPk(updatedRow);

          if (!pk) return;

          const originalRow = originalRowsMap[pk];

          if (!originalRow) return;

          if (hasEditableChanges(originalRow, updatedRow)) {
            nextPending[pk] = toApiPayload(updatedRow);
          } else {
            delete nextPending[pk];
          }
        });

        writeSessionPending(nextPending);

        return nextPending;
      });

      setChangedCells((prevChanged) => {
        const nextChanged = { ...prevChanged };

        updatedRows.forEach((updatedRow) => {
          const pk = getRowPk(updatedRow);

          if (!pk) return;

          const originalRow = originalRowsMap[pk];

          if (!originalRow) return;

          Array.from(EDITABLE_COLUMNS).forEach((col) => {
            const changed =
              normalizeForCompare(originalRow?.[col]) !==
              normalizeForCompare(updatedRow?.[col]);

            const cellKey = `${pk}-${col}`;

            if (changed) {
              nextChanged[cellKey] = true;
            } else {
              delete nextChanged[cellKey];
            }
          });
        });

        return nextChanged;
      });

      return updatedRows;
    });

    toast.success(
      `${adjustmentType === "increase" ? "Increased" : "Decreased"} ${column} by ${percentage}%${roundOff ? " and rounded off" : ""
      }`,
    );

    setPriceAdjustment(null);
    setAdjustmentPercentage("");
    setAdjustmentType("increase");
    setRoundOff(false);
  }, [
    priceAdjustment,
    adjustmentPercentage,
    adjustmentType,
    roundOff,
    originalRowsMap,
    writeSessionPending,
  ]);

  const applyBulkColumnValue = useCallback(() => {
    if (!bulkColumnAction || bulkValue === "") {
      toast.error("Please select a value");
      return;
    }

    const column = bulkColumnAction;

    setRows((prevRows) => {
      const updatedRows = prevRows.map((row) => {
        let value = bulkValue;

        // icon_dish / iconDish
        if (column === "icon_dish" || column === "iconDish") {
          value =
            bulkValue === "Veg"
              ? VEG_ICON_URL
              : NON_VEG_ICON_URL;
        }

        return {
          ...row,
          [column]: value,

          // Keep both icon fields synchronized
          ...(column === "icon_dish"
            ? { iconDish: value }
            : {}),

          ...(column === "iconDish"
            ? { icon_dish: value }
            : {}),
        };
      });

      // Update pending API changes
      setPendingChanges((prevPending) => {
        const nextPending = { ...prevPending };

        updatedRows.forEach((updatedRow) => {
          const pk = getRowPk(updatedRow);

          if (!pk) return;

          const originalRow = originalRowsMap[pk];

          if (!originalRow) return;

          if (hasEditableChanges(originalRow, updatedRow)) {
            nextPending[pk] = toApiPayload(updatedRow);
          } else {
            delete nextPending[pk];
          }
        });

        writeSessionPending(nextPending);

        return nextPending;
      });

      // Highlight changed cells
      setChangedCells((prevChanged) => {
        const nextChanged = { ...prevChanged };

        updatedRows.forEach((updatedRow) => {
          const pk = getRowPk(updatedRow);

          if (!pk) return;

          const originalRow = originalRowsMap[pk];

          if (!originalRow) return;

          Array.from(EDITABLE_COLUMNS).forEach((col) => {
            const changed =
              normalizeForCompare(originalRow?.[col]) !==
              normalizeForCompare(updatedRow?.[col]);

            const cellKey = `${pk}-${col}`;

            if (changed) {
              nextChanged[cellKey] = true;
            } else {
              delete nextChanged[cellKey];
            }
          });
        });

        return nextChanged;
      });

      return updatedRows;
    });

    toast.success(`${column} updated successfully`);

    setBulkColumnAction(null);
    setBulkValue("");
  }, [
    bulkColumnAction,
    bulkValue,
    originalRowsMap,
    writeSessionPending,
  ]);

  const saveAllChanges = async () => {
    if (pendingRequests.length === 0) {
      toast("No pending changes");
      return;
    }

    setSaving(true);

    const res = await updateCatalogueTableItems({
      requests: pendingRequests,
    });

    setSaving(false);

    if (res?.success) {
      toast.success(res.message || "Catalogue updated successfully");

      sessionStorage.removeItem(storageKey);
      setPendingChanges({});
      setChangedCells({});
      setSelectedCell(null);
      setEditingCell(null);

      await fetchPage({ nextOffset: 1, append: false });
    } else {
      toast.error(res?.message || "Failed to update catalogue");
    }
  };

  const discardPendingChanges = () => {
    sessionStorage.removeItem(storageKey);
    setPendingChanges({});
    setChangedCells({});
    setSelectedCell(null);
    setEditingCell(null);
    setDraftValue("");

    const restoredRows = rows.map((row) => {
      const pk = getRowPk(row);
      return originalRowsMap[pk] || row;
    });

    setRows(restoredRows);
    toast.success("Pending changes discarded");
  };

  const resetTableView = () => {
    const next = {};

    allColumns.forEach((col) => {
      next[col] = true;
    });

    setVisibleColumnMap(next);
    setShowReadonlyColumns(true);
    setSearchText("");
    setColumnSearchText("");
    setShowColumnPanel(false);
    setSelectedCell(null);
    setEditingCell(null);
    setDraftValue("");

    setTimeout(() => {
      if (filteredRows.length > 0 && allColumns.length > 0) {
        selectCell(0, 0);
        focusCell(0, 0);
      }
    }, 0);
  };

  const toggleColumnVisibility = (col) => {
    setVisibleColumnMap((prev) => ({
      ...prev,
      [col]: prev[col] === false,
    }));

    setSelectedCell(null);
    setEditingCell(null);
  };

  const showAllColumns = () => {
    const next = {};

    allColumns.forEach((col) => {
      next[col] = true;
    });

    setVisibleColumnMap(next);
    setShowReadonlyColumns(true);
    setSelectedCell(null);
    setEditingCell(null);
  };

  const showOnlyEditableColumns = () => {
    const next = {};

    allColumns.forEach((col) => {
      next[col] = isEditable(col);
    });

    setVisibleColumnMap(next);
    setShowReadonlyColumns(false);
    setSelectedCell(null);
    setEditingCell(null);
  };

  const showEssentialColumns = () => {
    const essential = new Set([
      "pk_item_id",
      "item_name",
      "item_description",
      "icon_dish",
      "iconDish",
      "category_name",
      "category_alias",
      "sub_category",
      "price",
      "discount_price",
      "size",
      "size_id",
      "stock",
      "inStock",
      "visibility",
      "barcode_number",
      "home_delivery_price",
      "takeaway_price",
      "dining_price",
      "room_order_price",
      "car_order_price",
    ]);

    const next = {};

    allColumns.forEach((col) => {
      next[col] = essential.has(col);
    });

    setVisibleColumnMap(next);
    setShowReadonlyColumns(true);
    setSelectedCell(null);
    setEditingCell(null);
  };

  useEffect(() => {
    setRows([]);
    setOriginalRowsMap({});
    setOffset(1);
    setHasMore(true);
    setSelectedCell(null);
    setEditingCell(null);
    setDraftValue("");
    setSearchText("");
    setColumnSearchText("");

    const stored = readSessionPending();
    setPendingChanges(stored);

    if (userID) {
      fetchPage({ nextOffset: 1, append: false });
    }
  }, [userID, fetchPage, readSessionPending]);

  useEffect(() => {
    if (!loaderRef.current || !hasMore || !userID) return;
    if (loading) return;
    if (filteredRows.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];

        if (first.isIntersecting && !loading && !loadingMore) {
          fetchPage({ nextOffset: offset + 1, append: true });
        }
      },
      { threshold: 0.25 },
    );

    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [
    offset,
    hasMore,
    loading,
    loadingMore,
    fetchPage,
    userID,
    filteredRows.length,
  ]);

  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();

      // Only input/textarea supports select()
      if (typeof inputRef.current.select === "function") {
        inputRef.current.select();
      }
    }
  }, [editingCell]);

  useEffect(() => {
    if (!selectedCell && filteredRows.length > 0 && visibleColumns.length > 0) {
      selectCell(0, 0);
    }
  }, [filteredRows.length, visibleColumns.length, selectedCell, selectCell]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        columnPanelRef.current &&
        !columnPanelRef.current.contains(event.target)
      ) {
        setShowColumnPanel(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!userID) {
    return (
      <div className="rounded-xl border bg-white py-16 text-center text-gray-400">
        Select client first.
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-white">
      {/* Header + Controls */}
      <div className="border-b bg-gray-50 px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-gray-800">
              Catalogue Editor
            </div>
            <div className="text-xs text-gray-500">
              Only approved catalogue fields are editable. Changes are saved as
              draft until you click Save Changes.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full border bg-white px-2.5 py-1 text-xs text-gray-600">
              Rows: {filteredRows.length}/{rows.length}
            </span>

            <span className="rounded-full border bg-white px-2.5 py-1 text-xs text-gray-600">
              Visible columns: {visibleColumns.length}/{allColumns.length}
            </span>

            <span className="rounded-full border bg-white px-2.5 py-1 text-xs text-gray-600">
              Readonly: {readonlyColumns.length}
            </span>

            {pendingCount > 0 && (
              <span className="rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                Pending items: {pendingCount}
              </span>
            )}

            {changedCellsCount > 0 && (
              <span className="rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                Changed cells: {changedCellsCount}
              </span>
            )}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            className="h-9 min-w-[190px] rounded-lg border bg-white px-3 text-sm outline-none focus:border-[#01C37D]"
            value={selectedUomCode}
            onChange={(e) => {
              setSelectedUomCode(e.target.value);
              setEditingCell(null);
              setDraftValue("");
            }}
          >
            <option value="">
              {uomLoading ? "Loading UOM..." : "Select UOM"}
            </option>

            {uomList.map((uom) => (
              <option key={uom.uom_id} value={uom.uom_code}>
                {uom.uom_name} ({uom.uom_code})
              </option>
            ))}
          </select>

          {selectedUomCode && (
            <span className="rounded-full border bg-white px-2.5 py-1 text-xs text-gray-600">
              {sizeLoading
                ? "Loading sizes..."
                : `${sizeOptions.length} sizes loaded`}
            </span>
          )}
          {/* Row search */}
          <div className="relative">
  <input
    className="h-9 w-[280px] rounded-lg border bg-white px-3 pr-8 text-sm outline-none focus:border-[#01C37D]"
    placeholder="Search catalogue rows..."
    value={searchText}
    onChange={(e) => {
      setSearchText(e.target.value);
      setSelectedCell(null);
      setEditingCell(null);
    }}
  />

  {searchLoading && (
    <div className="absolute right-3 top-1/2 -translate-y-1/2">
      <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-[#01C37D]" />
    </div>
  )}

  {!searchLoading && searchText && (
    <button
      type="button"
      className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-1 text-xs text-gray-400 hover:text-gray-700"
      onClick={() => {
        setSearchText("");
        setSelectedCell(null);
        setEditingCell(null);

        fetchPage({
          nextOffset: 1,
          append: false,
        });
      }}
    >
      ✕
    </button>
  )}
</div>

          {/* Column visibility */}
          <div className="relative" ref={columnPanelRef}>
            <button
              className="h-9 rounded-lg border bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={() => setShowColumnPanel((prev) => !prev)}
            >
              Select Columns
            </button>

            {showColumnPanel && (
              <div className="absolute left-0 top-11 z-50 w-[390px] rounded-xl border bg-white p-3 shadow-xl">
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-gray-800">
                      Select columns to show
                    </div>
                    <div className="text-xs text-gray-500">
                      Only checked columns will appear in the table.
                    </div>
                  </div>

                  <button
                    className="rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100"
                    onClick={() => setShowColumnPanel(false)}
                  >
                    Close
                  </button>
                </div>

                <input
                  className="mb-2 h-9 w-full rounded-lg border px-3 text-sm outline-none focus:border-[#01C37D]"
                  placeholder="Search columns..."
                  value={columnSearchText}
                  onChange={(e) => setColumnSearchText(e.target.value)}
                />

                <div className="mb-2 flex flex-wrap gap-2">
                  <button
                    className="rounded-lg border px-2.5 py-1 text-xs hover:bg-gray-50"
                    onClick={showAllColumns}
                  >
                    Show all
                  </button>

                  <button
                    className="rounded-lg border px-2.5 py-1 text-xs hover:bg-gray-50"
                    onClick={showEssentialColumns}
                  >
                    Essential
                  </button>

                  <button
                    className="rounded-lg border px-2.5 py-1 text-xs hover:bg-gray-50"
                    onClick={showOnlyEditableColumns}
                  >
                    Editable only
                  </button>

                  <button
                    className="rounded-lg border px-2.5 py-1 text-xs hover:bg-gray-50"
                    onClick={resetTableView}
                  >
                    Reset
                  </button>
                </div>

                <label className="mb-2 flex cursor-pointer items-center justify-between rounded-lg border bg-gray-50 px-3 py-2">
                  <span className="text-xs font-medium text-gray-700">
                    Show readonly columns
                  </span>
                  <input
                    type="checkbox"
                    checked={showReadonlyColumns}
                    onChange={(e) => {
                      setShowReadonlyColumns(e.target.checked);
                      setSelectedCell(null);
                      setEditingCell(null);
                    }}
                  />
                </label>

                <div className="max-h-[310px] overflow-auto rounded-lg border">
                  {filteredColumnOptions.length === 0 ? (
                    <div className="px-3 py-6 text-center text-xs text-gray-400">
                      No columns found.
                    </div>
                  ) : (
                    filteredColumnOptions.map((col) => {
                      const editable = isEditable(col);
                      const checked = visibleColumnMap[col] !== false;

                      return (
                        <label
                          key={col}
                          className="flex cursor-pointer items-center justify-between border-b px-3 py-2 last:border-b-0 hover:bg-gray-50"
                        >
                          <div className="min-w-0">
                            <div className="truncate text-xs font-medium text-gray-800">
                              {col}
                            </div>
                            <div className="text-[10px] text-gray-400">
                              {editable ? "editable" : "readonly"}
                            </div>
                          </div>

                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleColumnVisibility(col)}
                          />
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border bg-white px-3 text-sm text-gray-700 hover:bg-gray-50">
            <input
              type="checkbox"
              checked={showReadonlyColumns}
              onChange={(e) => {
                setShowReadonlyColumns(e.target.checked);
                setSelectedCell(null);
                setEditingCell(null);
              }}
            />
            Show readonly
          </label>

          <button
            className="h-9 rounded-lg border bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
            onClick={resetTableView}
          >
            Reset View
          </button>

          {pendingCount > 0 && (
            <>
              <button
                className="h-9 rounded-lg border border-red-200 bg-white px-3 text-sm font-medium text-red-600 hover:bg-red-50"
                onClick={discardPendingChanges}
                disabled={saving}
              >
                Discard Draft
              </button>

              <button
                className="h-9 rounded-lg bg-[#01C37D] px-4 text-sm font-semibold text-white hover:bg-[#00a96c] disabled:opacity-60"
                onClick={saveAllChanges}
                disabled={saving}
              >
                {saving ? "Saving..." : `Save Changes (${pendingCount})`}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Table */}
      {/* TOP HORIZONTAL SCROLLBAR */}
      <div
        ref={topScrollRef}
        className="w-full overflow-x-auto overflow-y-hidden"
        onScroll={(e) => {
          if (tableScrollRef.current) {
            tableScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
          }
        }}
      >
        <div
          style={{
            width: `${Math.max(visibleColumns.length * 170, 1000)}px`,
            height: "2px",
          }}
        />
      </div>

      {/* TABLE */}
      <div
        ref={tableScrollRef}
        className="h-[72vh] w-full overflow-x-auto overflow-y-auto"
        onScroll={(e) => {
          if (topScrollRef.current) {
            topScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
          }
        }}
      >
        <table className="min-w-max w-full table-fixed border-collapse text-xs">
          <thead className="sticky top-0 z-20 bg-[#f5f7fa] text-gray-700">
            <tr>
              {visibleColumns.map((col, colIndex) => {
                const editable = isEditable(col);

                return (
                  <th
                    key={col}
                    className="relative h-9 whitespace-nowrap border border-gray-200 px-3 text-left font-semibold"
                    style={{ width: colIndex === 0 ? 150 : 170 }}
                    title={col}
                  >
                    <div className="flex min-w-0 items-center gap-1">

                      {/* PRICE COLUMN */}
                      {PRICE_COLUMNS.has(col) ? (
                        <button
                          type="button"
                          className="flex min-w-0 items-center gap-1 rounded px-1 py-0.5 text-left hover:bg-emerald-50"
                          onClick={(e) => {
                            e.stopPropagation();

                            setPriceAdjustment((prev) =>
                              prev === col ? null : col
                            );

                            setBulkColumnAction(null);
                            setAdjustmentType("increase");
                            setAdjustmentPercentage("");
                            setRoundOff(false);
                          }}
                        >
                          <span className="truncate">{col}</span>
                          <span className="text-[10px] text-gray-400">
                            ▼
                          </span>
                        </button>

                      ) : BULK_SELECT_COLUMNS.has(col) ? (

                        /* VEG / STOCK / VISIBILITY */
                        <button
                          type="button"
                          className="flex min-w-0 items-center gap-1 rounded px-1 py-0.5 text-left hover:bg-emerald-50"
                          onClick={(e) => {
                            e.stopPropagation();

                            setBulkColumnAction((prev) =>
                              prev === col ? null : col
                            );

                            setPriceAdjustment(null);
                            setBulkValue("");
                          }}
                        >
                          <span className="truncate">{col}</span>
                          <span className="text-[10px] text-gray-400">
                            ▼
                          </span>
                        </button>

                      ) : (
                        <span className="truncate">{col}</span>
                      )}

                      {!editable && (
                        <span
                          className="shrink-0 rounded bg-gray-200 px-1 text-[10px] font-medium text-gray-500"
                          title="Readonly"
                        >
                          R
                        </span>
                      )}
                    </div>


                    {/* =============================== */}
                    {/* PRICE ADJUSTMENT DROPDOWN */}
                    {/* =============================== */}

                    {priceAdjustment === col && (
                      <div
                        className="absolute left-2 top-10 z-50 w-[240px] rounded-xl border border-gray-200 bg-white p-3 text-left shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="mb-3">
                          <div className="text-sm font-semibold text-gray-800">
                            Adjust Price
                          </div>

                          <div className="text-[10px] text-gray-500">
                            {col}
                          </div>
                        </div>

                        {/* Increase / Decrease */}
                        <div className="mb-3">
                          <label className="mb-1 block text-xs font-medium text-gray-600">
                            Action
                          </label>

                          <select
                            value={adjustmentType}
                            onChange={(e) =>
                              setAdjustmentType(e.target.value)
                            }
                            className="h-9 w-full rounded-lg border border-gray-300 bg-white px-2 text-xs outline-none focus:border-[#01C37D]"
                          >
                            <option value="increase">
                              Increase
                            </option>

                            <option value="decrease">
                              Decrease
                            </option>
                          </select>
                        </div>

                        {/* Percentage */}
                        <div className="mb-3">
                          <label className="mb-1 block text-xs font-medium text-gray-600">
                            Percentage
                          </label>

                          <div className="relative">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="Enter percentage"
                              value={adjustmentPercentage}
                              onChange={(e) =>
                                setAdjustmentPercentage(e.target.value)
                              }
                              className="h-9 w-full rounded-lg border border-gray-300 px-3 pr-8 text-xs outline-none focus:border-[#01C37D]"
                            />

                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                              %
                            </span>
                          </div>
                        </div>

                        {/* Round Off */}
                        <div className="mb-3">
                          <label className="flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                            <div>
                              <div className="text-xs font-semibold text-gray-700">
                                Round Off
                              </div>

                              <div className="text-[10px] text-gray-400">
                                Round to nearest whole number
                              </div>
                            </div>

                            <input
                              type="checkbox"
                              checked={roundOff}
                              onChange={(e) =>
                                setRoundOff(e.target.checked)
                              }
                              className="h-4 w-4 cursor-pointer accent-[#01C37D]"
                            />
                          </label>
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            className="h-8 rounded-lg border px-3 text-xs text-gray-600 hover:bg-gray-50"
                            onClick={() => {
                              setPriceAdjustment(null);
                              setAdjustmentPercentage("");
                              setAdjustmentType("increase");
                              setRoundOff(false);
                            }}
                          >
                            Cancel
                          </button>

                          <button
                            type="button"
                            className="h-8 rounded-lg bg-[#01C37D] px-3 text-xs font-semibold text-white hover:bg-[#00a96c]"
                            onClick={applyPriceAdjustment}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}


                    {/* =============================== */}
                    {/* VEG / STOCK / VISIBILITY DROPDOWN */}
                    {/* =============================== */}

                    {bulkColumnAction === col && (
                      <div
                        className="absolute left-2 top-10 z-50 w-[210px] rounded-xl border border-gray-200 bg-white p-3 text-left shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="mb-3">
                          <div className="text-sm font-semibold text-gray-800">
                            Update {col}
                          </div>

                          <div className="text-[10px] text-gray-500">
                            Apply to all loaded rows
                          </div>
                        </div>

                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          Select value
                        </label>

                        <select
                          value={bulkValue}
                          onChange={(e) =>
                            setBulkValue(e.target.value)
                          }
                          className="mb-3 h-9 w-full rounded-lg border border-gray-300 bg-white px-2 text-xs outline-none focus:border-[#01C37D]"
                        >
                          <option value="">
                            Select
                          </option>

                          {(col === "icon_dish" || col === "iconDish") ? (
                            <>
                              <option value="Veg">
                                Veg
                              </option>

                              <option value="Non Veg">
                                Non Veg
                              </option>
                            </>
                          ) : (
                            <>
                              <option value="Y">
                                Y
                              </option>

                              <option value="N">
                                N
                              </option>
                            </>
                          )}
                        </select>

                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            className="h-8 rounded-lg border px-3 text-xs text-gray-600 hover:bg-gray-50"
                            onClick={() => {
                              setBulkColumnAction(null);
                              setBulkValue("");
                            }}
                          >
                            Cancel
                          </button>

                          <button
                            type="button"
                            disabled={!bulkValue}
                            className="h-8 rounded-lg bg-[#01C37D] px-3 text-xs font-semibold text-white hover:bg-[#00a96c] disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={applyBulkColumnValue}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={Math.max(visibleColumns.length, 1)}
                  className="px-4 py-16 text-center text-gray-500"
                >
                  Loading table data...
                </td>
              </tr>
            ) : visibleColumns.length === 0 ? (
              <tr>
                <td className="px-4 py-16 text-center text-gray-400">
                  No columns selected. Use Select Columns or Reset.
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length}
                  className="px-4 py-16 text-center text-gray-400"
                >
                  No matching rows found.
                </td>
              </tr>
            ) : (
              filteredRows.map(({ row, sourceRowIndex, rowKey }, rowIndex) => (
                <tr
                  key={`${rowKey}-${sourceRowIndex}`}
                  className={rowIndex % 2 === 0 ? "bg-white" : "bg-[#fcfcfd]"}
                >
                  {visibleColumns.map((key, colIndex) => {
                    const selected =
                      selectedCell?.rowIndex === rowIndex &&
                      selectedCell?.colIndex === colIndex;

                    const editing =
                      editingCell?.rowIndex === rowIndex &&
                      editingCell?.colIndex === colIndex;

                    const editable = isEditable(key);
                    const value = row?.[key];
                    const numeric = isNumericLike(key, value);
                    const pk = getRowPk(row);
                    const changed = !!changedCells[`${pk}-${key}`];
                    const isIconDishColumn =
                      key === "icon_dish" || key === "iconDish";
                    const isSizeColumn = key === "size";

                    const currentIconDishValue = String(
                      row?.iconDish ?? row?.icon_dish ?? "",
                    );
                    const currentIconDishLabel =
                      ICON_DISH_OPTIONS.find(
                        (option) => option.value === currentIconDishValue,
                      )?.label || displayValue(value);

                    return (
                      <td
                        key={`${rowKey}-${key}`}
                        ref={(el) => {
                          cellRefs.current[`${rowIndex}-${colIndex}`] = el;
                        }}
                        tabIndex={0}
                        className={`h-9 border border-gray-200 px-2 align-middle outline-none ${numeric ? "text-right" : "text-left"
                          } ${editable ? "cursor-cell" : "bg-gray-50 text-gray-500"
                          } ${changed ? "bg-amber-50 text-amber-900" : ""} ${selected
                            ? "relative z-10 bg-emerald-50 ring-2 ring-inset ring-[#01C37D]"
                            : ""
                          }`}
                        onClick={() => selectCell(rowIndex, colIndex)}
                        onDoubleClick={() => startEdit(rowIndex, colIndex)}
                        onKeyDown={(e) => {
                          if (editing) return;

                          if (e.key === "ArrowUp") {
                            e.preventDefault();
                            moveSelection("up");
                          } else if (e.key === "ArrowDown") {
                            e.preventDefault();
                            moveSelection("down");
                          } else if (e.key === "ArrowLeft") {
                            e.preventDefault();
                            moveSelection("left");
                          } else if (e.key === "ArrowRight") {
                            e.preventDefault();
                            moveSelection("right");
                          } else if (e.key === "Tab") {
                            e.preventDefault();
                            moveSelection(e.shiftKey ? "left" : "right");
                          } else if (e.key === "Enter") {
                            e.preventDefault();

                            if (selected) {
                              startEdit(rowIndex, colIndex);
                            } else {
                              selectCell(rowIndex, colIndex);
                            }
                          } else if (e.key === "F2") {
                            e.preventDefault();
                            startEdit(rowIndex, colIndex);
                          }
                        }}
                      >
                        {editing ? (
                          isIconDishColumn ? (
                            <select
                              ref={inputRef}
                              className="h-7 w-full rounded border border-[#01C37D] bg-white px-2 text-xs outline-none"
                              value={currentIconDishValue}
                              onChange={(e) => {
                                const selectedValue = e.target.value;

                                commitCellPatch({
                                  patch: {
                                    [key]: selectedValue,
                                  },
                                });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Escape") {
                                  e.preventDefault();
                                  stopEdit();
                                  setTimeout(
                                    () => focusCell(rowIndex, colIndex),
                                    0,
                                  );
                                }
                              }}
                            >
                              <option value="">Select</option>

                              {ICON_DISH_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          ) : isSizeColumn ? (
                            <select
                              ref={inputRef}
                              className="h-7 w-full rounded border border-[#01C37D] bg-white px-2 text-xs outline-none"
                              value={String(row?.size_id ?? "")}
                              disabled={!selectedUomCode || sizeLoading}
                              onChange={(e) => {
                                const selectedSizeId = String(e.target.value);

                                const selectedSize = sizeOptions.find(
                                  (item) =>
                                    String(item.size_id) === selectedSizeId,
                                );

                                if (!selectedSize) return;

                                commitCellPatch({
                                  patch: {
                                    size: selectedSize.size_name,
                                    size_id: String(selectedSize.size_id),
                                  },
                                });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Escape") {
                                  e.preventDefault();
                                  stopEdit();
                                  setTimeout(
                                    () => focusCell(rowIndex, colIndex),
                                    0,
                                  );
                                }
                              }}
                            >
                              <option value="">
                                {!selectedUomCode
                                  ? "Select UOM first"
                                  : sizeLoading
                                    ? "Loading..."
                                    : "Select size"}
                              </option>

                              {sizeOptions.map((size) => (
                                <option
                                  key={size.size_id}
                                  value={String(size.size_id)}
                                >
                                  {size.size_name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              ref={inputRef}
                              className={`h-7 w-full rounded border border-[#01C37D] bg-white px-2 text-xs outline-none ${numeric ? "text-right" : "text-left"
                                }`}
                              value={draftValue}
                              onChange={(e) => setDraftValue(e.target.value)}
                              onBlur={() => saveEdit()}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  saveEdit("down");
                                } else if (e.key === "Tab") {
                                  e.preventDefault();
                                  saveEdit("right");
                                } else if (e.key === "Escape") {
                                  e.preventDefault();
                                  stopEdit();
                                  setTimeout(
                                    () => focusCell(rowIndex, colIndex),
                                    0,
                                  );
                                }
                              }}
                            />
                          )
                        ) : (
                          <div
                            className="truncate whitespace-nowrap leading-5"
                            title={
                              isIconDishColumn
                                ? currentIconDishValue
                                : displayValue(value)
                            }
                          >
                            {isIconDishColumn
                              ? currentIconDishLabel
                              : displayValue(value)}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div ref={loaderRef} className="py-4 text-center text-xs text-gray-500">
          {loadingMore
            ? "Loading more..."
            : hasMore
              ? "Scroll for more"
              : "No more data"}
        </div>
      </div>
    </div>
  );
}